import IDateRange from '@app/types/base/IDateRange';
import IOperatingTime, { IAnyOperatingTime, ILabeledOperatingTimes } from '@app/types/base/IOperatingTime';

export default class OperatingTimesUtil {
	public static mapOperatingTimes(
		operatingTimes: IAnyOperatingTime[] | undefined,
		i18n: Record<string, string>,
		locale: string,
		omitFullYearServices: boolean = false
	): string | undefined {
		if (!operatingTimes || !operatingTimes.length) {
			return undefined;
		}

		const reduceOperatingTimes = (result: string, operatingTime: IAnyOperatingTime): string => {
			let newResult: string = `${result}`;

			// process labeled operating times
			if ('label' in operatingTime) {
				if (omitFullYearServices && operatingTime.operatingTimes.every((ot) => !ot.period)) {
					return newResult;
				}

				if (newResult.length > 0) {
					newResult = `${newResult},`;
				}
				newResult = `${newResult} ${operatingTime.label} ${operatingTime.operatingTimes.reduce(
					reduceOperatingTimes,
					''
				)}`;
			}
			// process regular operating times
			else {
				if (omitFullYearServices && !operatingTime.period) {
					return newResult;
				}

				if (newResult.length > 0) {
					newResult = `${newResult} |`;
				}

				newResult = `${newResult} ${OperatingTimesUtil.mapOperatingTime(operatingTime, i18n, locale)}`;
			}

			return newResult;
		};

		const allOperatingTimesAreLabeled = operatingTimes.every((operatingTime) => 'label' in operatingTime);

		const maintenanceWindowsExist = operatingTimes.some((anyOperatingTime) => {
			if ('label' in anyOperatingTime) {
				return anyOperatingTime.operatingTimes.some(
					(operatingTime) => operatingTime.maintenanceWindows && operatingTime.maintenanceWindows.length > 0
				);
			}

			return anyOperatingTime.maintenanceWindows && anyOperatingTime.maintenanceWindows.length > 0;
		});

		const allOperatingTimesAreFullYear = operatingTimes.every((anyOperatingTime) => {
			if ('label' in anyOperatingTime) {
				return anyOperatingTime.operatingTimes.every((operatingTime) => !operatingTime.period);
			}
			return !anyOperatingTime.period;
		});

		if (allOperatingTimesAreLabeled) {
			const labeledOperatingTimes = operatingTimes as ILabeledOperatingTimes[];

			const allLabeledOperatingTimesAreEqual =
				Array.from(
					new Set(
						labeledOperatingTimes.map((operatingTime) => operatingTime.operatingTimes.reduce(reduceOperatingTimes, ''))
					)
				).length === 1;

			if (allLabeledOperatingTimesAreEqual) {
				// if multiple operating times are present (e.g. season 2023, season 2024) and all are full year, only show the first one
				if (allOperatingTimesAreFullYear) {
					// if no maintenance windows exist, return one full year operating time
					if (!maintenanceWindowsExist) {
						return labeledOperatingTimes[0].operatingTimes.slice(0, 1).reduce(reduceOperatingTimes, '') || undefined;
					}

					// if any maintenance windows exist, return no full year operating time. instead, omit operating times at all.
					return undefined;
				}

				return labeledOperatingTimes[0].operatingTimes.reduce(reduceOperatingTimes, '') || undefined;
			}
		}

		if (allOperatingTimesAreFullYear && maintenanceWindowsExist) {
			return undefined;
		}

		return operatingTimes.reduce(reduceOperatingTimes, '') || undefined;
	}

	public static mapMaintenanceTimes(
		operatingTimes: IAnyOperatingTime[] | undefined,
		i18n: Record<string, string>,
		locale: string
	): string | undefined {
		if (!operatingTimes || !operatingTimes.length) {
			return undefined;
		}

		const reduceMaintenancePeriods = (result: string, operatingTime: IAnyOperatingTime): string => {
			let newResult: string = `${result}`;

			if ('label' in operatingTime) {
				if (operatingTime.operatingTimes.flatMap((ot) => ot.maintenanceWindows || []).length === 0) {
					return newResult;
				}

				if (newResult.length > 0) {
					newResult = `${newResult},`;
				}
				newResult = `${newResult} ${operatingTime.label} ${operatingTime.operatingTimes.reduce(
					reduceMaintenancePeriods,
					''
				)}`;
			} else {
				if (!operatingTime.maintenanceWindows || operatingTime.maintenanceWindows.length === 0) {
					return newResult;
				}

				if (newResult.length > 0) {
					newResult = `${newResult} |`;
				}

				newResult = `${newResult} ${operatingTime.maintenanceWindows
					.map((window) => OperatingTimesUtil.mapDateRange(window, locale))
					.join(' | ')}`;
			}

			return newResult;
		};

		const allOperatingTimesAreLabeled = operatingTimes.every((operatingTime) => 'label' in operatingTime);

		if (allOperatingTimesAreLabeled) {
			const allLabeledOperatingTimeMaintenancePeriodsAreEqual =
				Array.from(
					new Set(
						(operatingTimes as ILabeledOperatingTimes[]).map((operatingTime) =>
							operatingTime.operatingTimes.reduce(reduceMaintenancePeriods, '')
						)
					)
				).length === 1;

			if (allLabeledOperatingTimeMaintenancePeriodsAreEqual) {
				return (operatingTimes[0] as ILabeledOperatingTimes).operatingTimes.reduce(reduceMaintenancePeriods, '');
			}
		}

		return operatingTimes.reduce(reduceMaintenancePeriods, '');
	}

	private static mapOperatingTime(operatingTime: IOperatingTime, i18n: Record<string, string>, locale: string): string {
		if (operatingTime.period) {
			return this.mapDateRange(operatingTime.period, locale);
		}

		if (operatingTime.type === 'train') {
			return i18n.openingTimeFullYearTrains;
		}

		return i18n.openingTimeFullYearVenues;
	}

	private static mapDateRange(dateRange: IDateRange, locale: string): string {
		type DateFormatOptions = { rangeDivider: string } & Omit<Intl.DateTimeFormatOptions, 'timeZone'>;

		// some locales require custom formatting as per jbm specifications:
		const customLocaleFormats: Record<string, DateFormatOptions> = {
			ja: {
				rangeDivider: '～',
				calendar: 'gregory',
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			},
			ko: {
				rangeDivider: '~',
				calendar: 'gregory',
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			},
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'zh-CN': {
				rangeDivider: '至',
				calendar: 'gregory',
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			},
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'zh-HK': {
				rangeDivider: '至',
				calendar: 'gregory',
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			}
		};

		// use custom formatting for specific locales
		if (Object.keys(customLocaleFormats).includes(locale)) {
			const localeOptions = customLocaleFormats[locale];
			const dateTimeFormat = new Intl.DateTimeFormat(locale, localeOptions);

			return `${dateTimeFormat.format(dateRange.start)} ${localeOptions.rangeDivider} ${dateTimeFormat.format(
				dateRange.end
			)}`;
		}

		// use default formatting for all other locales
		const dateTimeFormat = new Intl.DateTimeFormat('de', {
			calendar: 'gregory',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			timeZone: 'Europe/Zurich'
		});

		// format date range (don't use formatRange as years are explicity required to be shown event for same year dates)
		return `${dateTimeFormat.format(dateRange.start)} - ${dateTimeFormat.format(dateRange.end)}`;
	}
}
